import React, { useMemo } from "react";
import { Skeleton, type SkeletonProps } from "@mui/material";

type HeaderProps = React.TdHTMLAttributes<HTMLTableCellElement> & {
  key?: React.Key;
};

type TableSkeletonColumn = {
  id?: string;
  Header?: React.ReactNode;
  skeletonWidth?: SkeletonProps["width"];
  getHeaderProps: () => HeaderProps;
};

type RpTableSkeletonProps = {
  columns?: TableSkeletonColumn[];
  rowCount?: number;
  actionColumnIds?: string[];
};

const getSkeletonWidth = (column: TableSkeletonColumn, columnIndex: number) => {
  if (column.skeletonWidth) return column.skeletonWidth;
  if (columnIndex === 0) return "80%";
  return "75%";
};

export default function RpTableSkeleton({
  columns = [],
  rowCount = 5,
  actionColumnIds = ["action"],
}: RpTableSkeletonProps) {
  const skeletonRows = useMemo(
    () => Array.from({ length: rowCount }),
    [rowCount]
  );

  return skeletonRows.map((_, rowIndex) => (
    <tr key={`skeleton-row-${rowIndex}`}>
      {columns.map((column, columnIndex) => {
        const { key: skeletonCellKey, ...skeletonCellProps } =
          column.getHeaderProps();
        const isActionColumn =
          actionColumnIds.includes(column.id) || column.Header === "Action";

        return (
          <td
            key={`${skeletonCellKey}-skeleton-${rowIndex}`}
            {...skeletonCellProps}
          >
            <Skeleton
              variant={isActionColumn ? "circular" : "text"}
              height={isActionColumn ? 28 : 48}
              width={
                isActionColumn ? 28 : getSkeletonWidth(column, columnIndex)
              }
              animation="wave"
            />
          </td>
        );
      })}
    </tr>
  ));
}
